<template>
  <b-card>
    <b-row>
      <!-- User Info -->
      <b-col cols="12" class="d-flex flex-column flex-md-row align-items-center align-items-md-start justify-content-between">
        <!-- Avatar + Info -->
        <div class="d-flex flex-column flex-sm-row align-items-center text-center text-sm-left w-100">
          <b-avatar
            :src="userData?.avatar"
            size="104px"
            rounded
            class="mb-2 mb-sm-0"
          />
          <div class="d-flex flex-column ml-sm-2 mt-2 mt-sm-0 flex-grow-1">
            <div class="mb-1">
              <h4 class="mb-1 text-wrap">
                {{ userData?.name }}
              </h4>

              <span class="card-text d-block small text-muted">
                {{ userData?.employee_number }}
              </span>
              <span class="card-text d-block small text-muted">
                {{ userData?.email }}
              </span>
            </div>

            <div class="d-flex flex-wrap justify-content-center justify-content-sm-start mt-1">
              <template v-if="$permissionAbility(USERS_PROFILE_DETAILS_ACCESS, permissions)">
                <b-button
                  :to="{
                    name: 'admin-user-edit-details',
                    params: { id: this.$route.params.id },
                  }"
                  variant="primary"
                  size="sm"
                  class="mt-1 mt-sm-0"
                >
                  Show Profile Details
                </b-button>
              </template>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Details Table -->
      <b-col cols="12" class="mt-4">
        <div class="table-responsive">
          <table class="w-100 table table-borderless">
            <tbody>
              <tr>
                <th class="pb-50 text-nowrap">
                  <feather-icon icon="HashIcon" class="mr-75" />
                  <span class="font-weight-bold">Employee ID</span>
                </th>
                <td class="pb-50 text-break">
                  {{ userData?.employee_number }}
                </td>
              </tr>

              <tr>
                <th class="pb-50 text-nowrap">
                  <feather-icon icon="HashIcon" class="mr-75" />
                  <span class="font-weight-bold">Machine Employee ID</span>
                </th>
                <td class="pb-50 text-break">
                  {{ userData?.machine_user_id }}
                </td>
              </tr>

              <tr>
                <th class="pb-50 text-nowrap">
                  <feather-icon icon="BoxIcon" class="mr-75" />
                  <span class="font-weight-bold">Work Type</span>
                </th>
                <td class="pb-50 text-break">
                  {{ userData?.work_type }}
                </td>
              </tr>

              <tr>
                <th class="pb-50 text-nowrap">
                  <feather-icon icon="UserIcon" class="mr-75" />
                  <span class="font-weight-bold">Username</span>
                </th>
                <td class="pb-50 text-break">
                  {{ userData?.name }}
                </td>
              </tr>

              <tr>
                <th class="pb-50 text-nowrap">
                  <feather-icon icon="MailIcon" class="mr-75" />
                  <span class="font-weight-bold">Email</span>
                </th>
                <td class="pb-50 text-break">
                  {{ userData?.email }}
                </td>
              </tr>

              <tr>
                <th class="pb-50 text-nowrap">
                  <feather-icon icon="PhoneIcon" class="mr-75" />
                  <span class="font-weight-bold">Contact Number</span>
                </th>
                <td class="pb-50 text-break">
                  {{ userData?.mobile }}
                </td>
              </tr>

              <tr>
                <th class="pb-50 text-nowrap">
                  <feather-icon icon="CheckIcon" class="mr-75" />
                  <span class="font-weight-bold">Status</span>
                </th>
                <td class="pb-50">
                  <template v-if="userData?.status">
                    <b-badge variant="success" class="badge-glow">Active</b-badge>
                  </template>
                  <template v-else>
                    <b-badge variant="danger" class="badge-glow">Inactive</b-badge>
                  </template>
                </td>
              </tr>

              <tr v-if="userData?.terminated">
                <th class="pb-50 text-nowrap">
                  <feather-icon icon="CheckIcon" class="mr-75" />
                  <span class="font-weight-bold">Termination Status</span>
                </th>
                <td class="pb-50">
                  <b-badge
                    :variant="userData?.termination_type === 'terminated' ? 'danger' : 'warning'"
                    class="badge-glow text-capitalize"
                  >
                    {{ userData?.termination_type === 'terminated' ? 'Terminated' : 'Contract Ended' }}
                  </b-badge>
                </td>
              </tr>

              <tr>
                <th class="pb-50 text-nowrap">
                  <feather-icon icon="BoxIcon" class="mr-75" />
                  <span class="font-weight-bold">Division</span>
                </th>
                <td class="pb-50 text-capitalize text-break">
                  {{ userData?.department?.data?.division?.data?.name }}
                </td>
              </tr>

              <tr>
                <th class="pb-50 text-nowrap">
                  <feather-icon icon="PackageIcon" class="mr-75" />
                  <span class="font-weight-bold">Department</span>
                </th>
                <td class="pb-50 text-capitalize text-break">
                  {{ userData?.department?.data?.name }}
                </td>
              </tr>

              <tr>
                <th class="pb-50 text-nowrap">
                  <feather-icon icon="AwardIcon" class="mr-75" />
                  <span class="font-weight-bold">Designation</span>
                </th>
                <td class="pb-50 text-capitalize text-break">
                  {{ userData?.designation?.data?.name }}
                </td>
              </tr>

              <tr>
                <th class="pb-50 text-nowrap">
                  <feather-icon icon="UserCheckIcon" class="mr-75" />
                  <span class="font-weight-bold">Role</span>
                </th>
                <td class="pb-50 text-capitalize text-break">
                  {{ userData?.roles?.data.map(role => role.name).join(', ') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import { USERS_PROFILE_DETAILS_ACCESS } from "@/helpers/permissionsConstant";
import { BCard, BButton, BAvatar, BRow, BCol, BBadge } from "bootstrap-vue";
import UserAdditionalDetails from "./UserAdditionalDetails.vue";
import { mapGetters } from "vuex";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: "UserInfoCard",
  components: { BCard, BButton, BRow, BCol, BAvatar, UserAdditionalDetails, BBadge },
  data() {
    return { USERS_PROFILE_DETAILS_ACCESS, userData: {} };
  },
  computed: {
    ...mapGetters({ permissions: "userModule/getPermissions" }),
  },
  async created() {
    try {
      const [userGeneralData] = await Promise.all([this.getUserGeneralData()]);
      this.userData = userGeneralData?.data?.data;
    } catch (error) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Error",
          icon: "BellIcon",
          variant: "danger",
          text: error?.response?.data?.message,
        },
      });
    }
  },
  methods: {
    async getUserGeneralData() {
      return await this.$api.get(
        `api/users/${this.$route.params.id}?include=department.division,roles,employeeType,designation,leaveTypes`
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.table-responsive {
  overflow-x: auto;
}

table th,
table td {
  vertical-align: middle;
  padding: 0.75rem 0.5rem;
}

@media (max-width: 767.98px) {
  h4 {
    font-size: 1.1rem;
  }

  table th {
    width: 40%;
    white-space: nowrap;
  }

  table td {
    width: 60%;
    word-break: break-word;
  }

  .b-avatar {
    width: 80px !important;
    height: 80px !important;
  }
}
</style>
